import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from "react";
import { ThemeProvider } from "@mui/material/styles";
import LightTheme from "../theme/LightTheme";
import DarkTheme from "../theme/DarkTheme";

type ColorModeContextType = {
  mode: "light" | "dark";
  setMode: () => void;
};

// Create context with proper default values
const ColorModeContext = createContext<ColorModeContextType | undefined>(undefined);

// Custom hook to use the context safely
export const useColorMode = (): ColorModeContextType => {
  const context = useContext(ColorModeContext);
  if (!context) {
    throw new Error("useColorMode must be used within a ColorModeContextProvider");
  }
  return context;
};

export const ColorModeContextProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  // Helper function to get initial mode
  const getInitialMode = (): "light" | "dark" => {
    try {
      const colorModeFromLocalStorage = localStorage.getItem("colorMode");
      if (colorModeFromLocalStorage === "light" || colorModeFromLocalStorage === "dark") {
        return colorModeFromLocalStorage;
      }
    } catch (error) {
      console.warn("Could not access localStorage:", error);
    }

    // Fallback to system preference
    const prefersDarkMode = window.matchMedia("(prefers-color-scheme: dark)").matches;
    return prefersDarkMode ? "dark" : "light";
  };

  const [mode, setModeState] = useState<"light" | "dark">(getInitialMode);

  // Update localStorage whenever mode changes
  useEffect(() => {
    try {
      localStorage.setItem("colorMode", mode);
    } catch (error) {
      console.warn("Could not save to localStorage:", error);
    }
  }, [mode]);

  const toggleMode = () => {
    console.log("toggleMode called, current mode:", mode);
    setModeState(prevMode => {
      const newMode = prevMode === "light" ? "dark" : "light";
      console.log("Switching from", prevMode, "to", newMode);
      return newMode;
    });
  };

  const contextValue: ColorModeContextType = {
    mode,
    setMode: toggleMode,
  };

  console.log("Context value:", contextValue);

  return (
    <ColorModeContext.Provider value={contextValue}>
      <ThemeProvider theme={mode === "light" ? LightTheme : DarkTheme}>
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};

// Also export the context for direct use if needed (but prefer the hook)
export { ColorModeContext };